import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  getApiConfig,
  setApiEnvironment as saveApiEnvironment,
  type ApiEnvironment,
} from "../config/api";

interface ApiContextType {
  environment: ApiEnvironment;
  isRefreshing: boolean;
  switchEnvironment: (newEnv: ApiEnvironment) => void;
  refreshTrigger: number;
  triggerRefresh: () => void;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [environment, setEnvironment] = useState<ApiEnvironment>(
    () => getApiConfig().environment
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const switchEnvironment = useCallback(
    (newEnv: ApiEnvironment) => {
      setIsRefreshing(true);
      saveApiEnvironment(newEnv);
      setEnvironment(newEnv);

      // Trigger a refresh in all components that are listening
      setRefreshTrigger((prev) => prev + 1);

      // Show loading for at least 500ms for better UX
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    },
    []
  );

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const value = useMemo(
    () => ({
      environment,
      isRefreshing,
      switchEnvironment,
      refreshTrigger,
      triggerRefresh,
    }),
    [
      environment,
      isRefreshing,
      switchEnvironment,
      refreshTrigger,
      triggerRefresh,
    ]
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};
